import {
	ReactElement,
	useCallback,
	useState
} from "react";
import { IntegrationFlowMultipleAuthenticationsAreRequiredModel } from "client/Api/IntegrationBuilder/integrationBuilderAuthentication";
import { BusyIndicator } from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import {
	AuthenticateIntegrationFlowResultKind,
	useTryAuthenticateIntegrationFlow
} from "client/Features/Layout/TitleBar/GlobalSearch/useAuthenticateIntegrationFlow";
import { LiveTileContent } from "../LiveTileContent";

export type AuthenticationRequiredProps = {
	appName: string;
	authenticationModel: IntegrationFlowMultipleAuthenticationsAreRequiredModel;
	onAuthenticated: () => void;
};

export const AuthenticationRequired = (props: AuthenticationRequiredProps): ReactElement => {
	const {
		appName,
		authenticationModel,
		onAuthenticated
	} = props;

	const [isAuthenticating, setIsAuthenticating] = useState<boolean>(false);

	const [wasInvoked, setWasInvoked] = useState<boolean>(false);

	const [authenticationDialogs, authenticate] = useTryAuthenticateIntegrationFlow();

	const authenticateHandler = useCallback(
		async (): Promise<void> => {
			if (isAuthenticating) {
				return;
			}

			const {
				authentications: missingAuthentications
			} = authenticationModel;

			const result: TryResult<AuthenticateIntegrationFlowResultKind> = await authenticate(
				appName,
				missingAuthentications,
				setIsAuthenticating
			);

			if (!result.wasSuccessful) {
				return;
			}

			switch (result.result) {
				case "completed":
					onAuthenticated();
					break;
				case "wasInvoked":
					setWasInvoked(true);
					break;
				default:
					never(result.result);
			}
		},
		[
			isAuthenticating,
			authenticationModel,
			appName,
			authenticate,
			onAuthenticated
		]
	);

	const retryConfiguration: RetryConfiguration = wasInvoked
		? {
			onRetry: onAuthenticated,
			retryText: localize("_Finish")
		}
		: {
			onRetry: authenticateHandler,
			retryText: localize("_Authenticate")
		};

	return (
		<>
			<LiveTileContent.NoResults
				{...retryConfiguration}
				trackingId="authentication-required"
				icon="gear"
				title={localize("_AuthenticationRequired")}
				message={{
					template: localize("_ToUseNameAuthenticationIsNeeded"),
					replacements: [
						{ what: "{{Name}}", filler: appName }
					]
				}}
			/>
			{isAuthenticating && <BusyIndicator />}
			{authenticationDialogs}
		</>
	);
};
AuthenticationRequired.displayName = "AuthenticationRequired";

type RetryConfiguration = {
	onRetry: () => void;
	retryText: string;
};
